/**
 * GoogleAdSenseLoader Component
 *
 * Internal component that conditionally loads Google AdSense for non-premium users.
 * This component must be rendered inside AuthProvider to access authentication context.
 *
 * NOTE: Server-side blocking is now handled in adsenseMiddleware.ts
 * This component only handles client-side cleanup for premium users who might
 * have cached HTML with AdSense scripts.
 */
import React, { useEffect } from 'react';
import { useGoogleAdSense } from '../hooks/useGoogleAdSense';
import { useAdSvgFixer } from '../hooks/useAdSvgFixer';
import { useSubscription } from '../hooks/useSubscription';
import { useFundingChoicesBlocker } from '../hooks/useFundingChoicesBlocker';
import { logger } from "../utils/logger";
import { isViteAdSenseSuppressGoogleOverlaysEnabled } from '../../shared/adsense/adSurfacePolicy';
import { startGoogleOverlayAdsSuppressor } from '../ads/suppressGoogleOverlayAds';
import { useAdSenseConsentGate } from '../ads/useAdSenseConsentGate';

/** Runs AdSense hooks only after consent soft-gate opens (avoids guest isLoading bypass). */
const GoogleAdSenseLoaderInner: React.FC = () => {
    const { isAdFree, isLoading: subscriptionLoading, premiumStatus, adEligibility } = useSubscription();

    useEffect(() => {
        const isServerBlocked = document.documentElement.getAttribute('data-premium-user') === 'true' ||
            document.documentElement.getAttribute('data-adsense-blocked') === 'true' ||
            document.documentElement.getAttribute('data-k9-user') === 'true';
        logger.debug('[GoogleAdSenseLoader] Component mounted', { "arg0": {
                isAdFree,
                subscriptionLoading,
                isServerBlocked,
                premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded yet',
                adEligibility: adEligibility ? JSON.stringify(adEligibility) : 'not loaded yet',
                serverAttributes: {
                    'data-premium-user': document.documentElement.getAttribute('data-premium-user'),
                    'data-adsense-blocked': document.documentElement.getAttribute('data-adsense-blocked'),
                    'data-funding-choices-blocked': document.documentElement.getAttribute('data-funding-choices-blocked')
                }
            } });
        return () => {
            logger.debug('[GoogleAdSenseLoader] Component unmounted');
        };
    }, []);

    useEffect(() => {
        const isServerBlocked = document.documentElement.getAttribute('data-premium-user') === 'true' ||
            document.documentElement.getAttribute('data-adsense-blocked') === 'true' ||
            document.documentElement.getAttribute('data-k9-user') === 'true';
        logger.debug('[GoogleAdSenseLoader] Server-side blocking check', { "arg0": {
                isServerBlocked,
                isAdFree,
                willRemoveScripts: isServerBlocked || isAdFree
            } });
        if (isServerBlocked || isAdFree) {
            const scripts = document.querySelectorAll('script[src*="googlesyndication"], script[src*="adsbygoogle"]');
            if (scripts.length > 0) {
                logger.debug(`[GoogleAdSenseLoader] 🚫 Removing ${scripts.length} cached AdSense scripts`);
                scripts.forEach(script => script.remove());
            }
        }
    }, [isAdFree]);

    useEffect(() => {
        if (isAdFree || subscriptionLoading) {
            return;
        }
        if (!isViteAdSenseSuppressGoogleOverlaysEnabled(import.meta.env.VITE_ADSENSE_SUPPRESS_GOOGLE_OVERLAYS)) {
            return;
        }
        return startGoogleOverlayAdsSuppressor();
    }, [isAdFree, subscriptionLoading]);

    useFundingChoicesBlocker();

    const isServerBlocked = typeof document !== 'undefined' &&
        (document.documentElement.getAttribute('data-premium-user') === 'true' ||
            document.documentElement.getAttribute('data-adsense-blocked') === 'true' ||
            document.documentElement.getAttribute('data-k9-user') === 'true');

    useGoogleAdSense(isAdFree, isServerBlocked, subscriptionLoading);
    useAdSvgFixer();
    return null;
};

export const GoogleAdSenseLoader: React.FC = () => {
    const consentGateReady = useAdSenseConsentGate(5000);
    if (!consentGateReady) {
        return null;
    }
    return <GoogleAdSenseLoaderInner />;
};

export default GoogleAdSenseLoader;
